import apiClient from '../apiClient';
import type { ApiResponse, RevenueDataPoint, RevenueByCompany, RevenueByMachine } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/analytics';

export const getRevenue = async (
  period: 'daily' | 'weekly' | 'monthly' = 'daily',
  dateFrom?: string,
  dateTo?: string,
  companyId?: string
): Promise<RevenueDataPoint[]> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/revenue`, {
    period,
    dateFrom,
    dateTo,
    companyId,
  });
  const { data } = await apiClient.get<ApiResponse<RevenueDataPoint[]>>(url);
  return data.data;
};

export const getRevenueByCompany = async (
  dateFrom?: string,
  dateTo?: string
): Promise<RevenueByCompany[]> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/revenue/by-company`, {
    dateFrom,
    dateTo,
  });
  const { data } = await apiClient.get<ApiResponse<RevenueByCompany[]>>(url);
  return data.data;
};

export const getRevenueByMachine = async (
  dateFrom?: string,
  dateTo?: string,
  companyId?: string
): Promise<RevenueByMachine[]> => {
  const url = getUrlWithQueryParams(`${BASE_URL}/revenue/by-machine`, {
    dateFrom,
    dateTo,
    companyId,
  });
  const { data } = await apiClient.get<ApiResponse<RevenueByMachine[]>>(url);
  return data.data;
};

// React Query Hooks

export const useGetRevenue = (
  period: 'daily' | 'weekly' | 'monthly' = 'daily',
  dateFrom?: string,
  dateTo?: string,
  companyId?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.ANALYTICS_REVENUE, period, dateFrom, dateTo, companyId],
    queryFn: () => getRevenue(period, dateFrom, dateTo, companyId),
  });

export const useGetRevenueByCompany = (dateFrom?: string, dateTo?: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.ANALYTICS_REVENUE_BY_COMPANY, dateFrom, dateTo],
    queryFn: () => getRevenueByCompany(dateFrom, dateTo),
  });

export const useGetRevenueByMachine = (
  dateFrom?: string,
  dateTo?: string,
  companyId?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.ANALYTICS_REVENUE_BY_MACHINE, dateFrom, dateTo, companyId],
    queryFn: () => getRevenueByMachine(dateFrom, dateTo, companyId),
  });
